import { Injectable } from '@angular/core';
import {
  ITransactionDefinition,
  ITransactionDefinitionClient,
  ITransactionDefinitionDoc,
} from '@app/transaction-builder/model/transaction-definition.model';
import {
  ITxFormClientData,
  ITxFormSigningRequest,
} from '@app/transaction-builder/model/transaction-form-data.model';
import { IClientSignField, ISignField } from '@app/transaction-builder/model/sign-field.model';
import { ITransactionData } from '@app/transaction-builder/model/transaction-data.model';
import { ClientDocumentRelation, SigningRequestChannel } from 'ngx-mat-esign360';
import { ProfileService } from '@app/core/services/profile.service';
import { IClientFieldsVisibility } from '@app/core/model/env-config.model';
import { isClientFieldVisible } from '../utils/form.util';

@Injectable({
  providedIn: 'root',
})
export class TransformTransactionDataService {
  constructor(private profileService: ProfileService) {}

  transactionDataToTransactionDefinition(
    transactionData: ITransactionData,
    startTransaction: boolean
  ): ITransactionDefinition {
    if (
      !transactionData.formData ||
      !transactionData.documentsData ||
      !transactionData.signFieldsByDocument
    ) {
      throw new Error(
        'Cannot convert to transaction definition where form data / documents data / sign fields are undefined.'
      );
    }

    return {
      name: transactionData.formData.name,
      clients: this.txDataClientsToTxDefinitionClients(transactionData.formData.clients),
      invokingUser: {
        ...transactionData.formData.invokingUser,
        email: transactionData.formData.invokingUser.email || undefined,
      },
      documents: this.transDataDocsToTxDefinitionDocs(transactionData),
      startTransaction,
    };
  }

  txDataClientsToTxDefinitionClients(clients: ITxFormClientData[]): ITransactionDefinitionClient[] {
    const clientFieldsVisibility = this.profileService.getCurrentProfile().clientFieldsVisibility;

    return clients.map((client) => {
      // remove cid from client
      const { cid, signingRequest, ...clientPersonalData } = client;
      const { channel: signingRequestChannel } = signingRequest;

      const txDefinitionClient = {
        ...clientPersonalData,
        signingRequestChannel: signingRequestChannel,
        signingRequestText: this.getTxDefinitionSigningRequestText(
          signingRequest,
          clientFieldsVisibility
        ),
      };

      if (
        !clientFieldsVisibility.signingRequestBody ||
        (!clientFieldsVisibility.signingRequestSubject &&
          txDefinitionClient.signingRequestChannel === SigningRequestChannel.Email)
      ) {
        delete txDefinitionClient.signingRequestText;
      }

      if (!clientFieldsVisibility.birthYear) {
        delete txDefinitionClient.birthYear;
      }

      return txDefinitionClient;
    });
  }

  private getTxDefinitionSigningRequestText(
    signingRequest: ITxFormSigningRequest,
    clientFieldsVisibility: IClientFieldsVisibility
  ): string {
    const { channel: signingRequestChannel, ...signingRequestWithoutChannel } = signingRequest;

    return JSON.stringify({
      subject: isClientFieldVisible(clientFieldsVisibility, 'signingRequestSubject')
        ? signingRequestWithoutChannel.subject
        : this.profileService.getCurrentProfile().defaultRequestSignatureSubject,

      body: isClientFieldVisible(clientFieldsVisibility, 'signingRequestBody')
        ? signingRequestWithoutChannel.body
        : this.profileService.getCurrentProfile().defaultRequestSignatureBody,
    });
  }

  transDataDocsToTxDefinitionDocs(transactionData: ITransactionData): ITransactionDefinitionDoc[] {
    return transactionData.documentsData.map((documentData, docIndex) => ({
      name: documentData.name,
      content: documentData.content.split(',')[1],
      signers: transactionData.formData.documents.clientsToDocs[docIndex].map(
        (relation, relIndex) => {
          const docSigner = transactionData.formData.clients[relIndex];
          return {
            personalId: docSigner.personalId,
            relation,
            signFields:
              relation === ClientDocumentRelation.MustSign
                ? this.extractSignerSignFields(
                    transactionData.signFieldsByDocument[documentData.cid],
                    docSigner
                  )
                : null,
          };
        }
      ),
    }));
  }

  private extractSignerSignFields(
    signFields: IClientSignField[],
    docSigner: ITxFormClientData
  ): ISignField[] {
    return signFields
      .filter((signField) => signField.client.cid === docSigner.cid)
      .map((filteredSignField) => this.clientSignFieldToSignField(filteredSignField));
  }

  clientSignFieldToSignField(clientSignField: IClientSignField): ISignField {
    const signField: ISignField = {
      pageIndex: clientSignField.pageIndex,
      topLeft: clientSignField.topLeft,
      bottomRight: clientSignField.bottomRight,
      type: clientSignField.type,
    };

    if (this.profileService.getCurrentProfile().canChangeSignFieldPriority) {
      return {
        ...signField,
        priority: clientSignField?.priority,
      };
    }

    return signField;
  }
}
